import { BorderlessButton } from 'components';
import {
  MeWithTutorialQuery_me_attendees,
  SceneStatusEnum,
  TrainingType,
} from 'graphql/types';

import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import { StartMatchContext } from 'pages/Lobby/LobbyGame/StartMatchProvider';

import MatchViewMessage from './MatchViewMessage';

export enum SceneStatusFilterAll {
  ALL = 'ALL',
}

export type SceneStatusFilterEnum = SceneStatusEnum | SceneStatusFilterAll;

export interface MatchViewProps {
  attendees: MeWithTutorialQuery_me_attendees[];
  isActiveTraining: boolean;
  opponentCount?: number;
}

const MatchViewMessages = ({
  attendees,
  isActiveTraining,
  opponentCount,
}: MatchViewProps) => {
  const { t } = useTranslation();

  const IS_EDU = true; // component is used on this layout only for now
  const LOCIZE_SUFFIX_KEY = IS_EDU ? '.edu' : '';

  const { loading, opponent, stopMatchmaking } = useContext(StartMatchContext);

  const nextMessages = [];

  const notCompletedAttendees = attendees.filter(
    (a) => !a.completedAt && a.trainingType === TrainingType.TRAINING,
  );
  const notCompletedTournaments = attendees.filter(
    (a) => !a.completedAt && a.trainingType === TrainingType.TOURNAMENT,
  );

  if (notCompletedTournaments.length) {
    // Messages about tournament
    notCompletedTournaments.forEach((attendee, index) => {
      nextMessages.push(
        <MatchViewMessage
          key={`nct_${index}`}
          icon={SceneStatusEnum.TOURNAMENT}
          level="WARN"
        >
          {t(`matchView.message.tournament${LOCIZE_SUFFIX_KEY}`, {
            name: attendee.trainingName,
            deadline: format(new Date(attendee.trainingDeadline), 'd.M.y'),
          })}
        </MatchViewMessage>,
      );
    });
  }

  if (notCompletedAttendees.length) {
    // Messages about not completed trainings
    notCompletedAttendees.forEach((attendee, index) => {
      nextMessages.push(
        <MatchViewMessage
          key={`nca_${index}`}
          icon={SceneStatusEnum.TRAINING}
          level="WARN"
        >
          {t('matchView.message.training', {
            name: attendee.trainingName,
            deadline: format(new Date(attendee.trainingDeadline), 'd.M.y'),
          })}
        </MatchViewMessage>,
      );
    });
  }

  if (loading) {
    // Waiting for invited user
    nextMessages.push(
      <MatchViewMessage key="matchWaiting" level="INFO">
        {opponent == null
          ? t('lobby.waitingForOpponent', 'Čekám na vhodného protihráče')
          : t('lobby.waitingForInvitedOpponent', {
              defaultValue: 'Čekám na {{opponent}}',
              opponent: opponent.user.name,
            })}
        <BorderlessButton
          labelT={{
            key: 'lobby.cancelMatchmaking',
            defaultValue: 'Zrušit čekání',
          }}
          onClick={stopMatchmaking!}
        />
      </MatchViewMessage>,
    );
  }

  if (
    attendees.length > 0 &&
    !notCompletedAttendees.length &&
    !notCompletedTournaments.length
  ) {
    // All trainings are completed
    nextMessages.push(
      <MatchViewMessage
        key="completed_training"
        icon={SceneStatusEnum.TRAINING}
        level="INFO"
      >
        <Trans t={t} i18nKey="matchView.trainingCompleted" />
      </MatchViewMessage>,
    );
  } else if (!attendees.length && isActiveTraining) {
    // Company has active training and it is not assigned to the current user
    nextMessages.push(
      <MatchViewMessage
        key="no_training_assigned"
        icon={SceneStatusEnum.TRAINING}
        level="INFO"
      >
        <span>{t('matchView.noActiveTraining')}</span>
      </MatchViewMessage>,
    );
  }

  if (opponentCount) {
    // Msg about online users
    nextMessages.push(
      <MatchViewMessage key="opponent_count" level="INFO">
        {t('lobby.onlineUsersCount', '{{count}} kolegů je online', {
          count: opponentCount,
        })}
      </MatchViewMessage>,
    );
  }

  return <>{nextMessages}</>;
};

export default MatchViewMessages;
